import localService from "@/services/localService";
import { findShapePdfByKey, isPresignedUrlExpired } from "@/utils/policyForms";

/*
  The only per-app file behind the shared comparison code. The store module, the
  services and the components differ between BWB and CWB through these names alone.
*/

export const STORE_NAMESPACE = "coverageComparison";

/**
 * Whether the quote comparison is switched on for this app. Always true: the page is shown
 * to everyone, and the API answers 404 when its own backend switch is off. The function
 * stays because the shared components import it as part of the adapter contract.
 */
export const isComparisonEnabled = () => true;

/** The signed-in user's id, or "" when nobody is signed in. */
export const currentUserId = () => {
  try {
    return String(localService.getItem("userInfo")?.user?.entity_id || "");
  } catch (e) {
    return "";
  }
};

/** The multipart endpoint a quote PDF is attached through, below the submission URL. */
export const QUOTE_DOCUMENT_UPLOAD_PATH = "/documents";

/** The root action that re-reads the submission's detail after a quote PDF is attached. */
export const SUBMISSION_DETAIL_ACTION = "brokerSubmission/fetchSubmissionDetail";

const documentIn = (detail, quoteId, documentKey) => {
  const row = ((detail && detail.quotes) || []).find((q) => q.platformQuoteId === quoteId || q.quoteId === quoteId);
  const found = row && findShapePdfByKey({ detail: row.detail || {} }, documentKey);
  const url = found && found.pdf && found.pdf.url;
  return url ? { url, fileName: found.pdf.filename || "", documentType: found.kind } : null;
};

/**
 * Resolves a citation among the quote's own documents. The submission detail the page
 * already holds is used while its link is still good; otherwise it is read again. When
 * that read fails, the link the page already had is still the best one there is.
 * A document the quote no longer lists is not found: the API has no endpoint for it here.
 * A miss comes back as `{ url: "", unavailable }`, "replaced" when the quote was read and
 * no longer lists the document, "fetch_failed" when it could not be read at all.
 */
export const resolveCitationLocally = async (
  dispatch,
  { orgId, submissionId, quoteId, documentKey },
  { rootState } = {}
) => {
  const cache = (rootState && rootState.brokerSubmission && rootState.brokerSubmission.submissionDetailCache) || {};
  const held = documentIn(cache[submissionId], quoteId, documentKey);
  if (held && !isPresignedUrlExpired(held.url)) return held;

  const detail = await dispatch(SUBMISSION_DETAIL_ACTION, { orgId, submissionId, refresh: true }, { root: true });
  if (!detail) return held || { url: "", unavailable: "fetch_failed" };
  return documentIn(detail, quoteId, documentKey) || { url: "", unavailable: "replaced" };
};

/** The quote-to-display-rows builder lives in a different module in each app. */
export { buildCoverageDisplayRows } from "@/services/quoteNormalizationService";

/** The path, below a submission's scan summary URL, of one scan report's PDF. */
export const scanDocumentPath = (documentId) => `/documents/${encodeURIComponent(documentId)}`;

/** The scan summary as the shared screen reads it: this API already names reports `documentId`. */
export const adaptScanSummary = (summary) => summary;
